import classNames from "classnames";
import { forwardRef, useId, InputHTMLAttributes } from "react";
import { ReactComponent as TickIcon } from "@icons/tick.svg";

export enum CheckBoxPosition {
  Left,
  Right
}

export interface CheckboxProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  helperText?: string;
  className?: string;
  position?: CheckBoxPosition
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  ({ label, position = CheckBoxPosition.Left, helperText, id, disabled, checked, className, ...props }, ref) => {
    const generatedId = useId();
    const finalId = id ?? generatedId;

    return (
      <div className={classNames("p-1 relative space-y-1 w-full", { "cursor-not-allowed": disabled }, { "cursor-pointer": !disabled })}>
        {label && (
          <label
            data-testid="label"
            htmlFor={finalId}
            className={classNames("font-bold text-navy-blue text-base flex items-center gap-2",
              { "justify-start": position === CheckBoxPosition.Left },
              { "justify-between": position === CheckBoxPosition.Right },
              { "opacity-50 cursor-not-allowed pointer-events-none": disabled },
              { "cursor-pointer": !disabled })
            }
          >
            {position === CheckBoxPosition.Right && <div data-testid="checkbox-position-right-label">{label}</div>}
            <div className="flex relative">
              <input
                ref={ref}
                id={finalId}
                type="checkbox"
                aria-describedby={`aria-${finalId}`}
                className={classNames(
                  "appearance-none h-6 w-6 rounded-[4px] border border-brand-purple",
                  "outline-none",
                  "ring-0 focus:ring-0 focus:outline-1 focus:outline-brand-purple",
                  { "bg-brand-purple": checked },
                  { "bg-l-blue": !checked },
                  { "cursor-not-allowed": disabled }, { "cursor-pointer": !disabled },
                  className
                )}
                disabled={disabled}
                checked={checked}
                {...props}
              />
              {
                checked && <span className="absolute h-6 w-6 flex items-center justify-center pointer-events-none">
                  <TickIcon className="text-white" />
                </span>
              }
            </div>
            {position === CheckBoxPosition.Left && <div data-testid="checkbox-position-left-label">{label}</div>}
          </label>
        )}

        {helperText && (
          <p id={`aria-${finalId}`} className={classNames("text-navy-blue text-sm mt-1")}>
            {helperText}
          </p>
        )}
      </div>
    );
  }
);
